import {
  Breakpoint,
  BreakpointListener,
  Breakpoints,
  Option,
  RadioGroup,
} from '@op/opds.opux';
import { space } from '@op/opds.tokens';
import React, { ReactElement, useMemo, useState } from 'react';
import { Message, useMessage } from '@messageformat/react';
import { IconInformation } from '@op/opds.opux-icons/indicator';
import CourseTaskHeader from '../CourseTaskHeader';
import { ReactComponent as Image013 } from '../../images/OP_symbolikuvitus_128_013.svg';
import TaskOneGraph from './TaskOneGraph';
import CourseContent from '../CourseContent';
import parseMsgContent from '../../util/parseMsgContent';

type Content = {
  columns: number;
};

const TaskOne = (): ReactElement => {
  const [value, setValue] = React.useState('');
  const [breakpoint, setBreakpoint] = useState<Breakpoint | null>(null);
  const [showQuestionOneResult, setShowQuestionOneResult] = useState(false);
  const taskOneIntroText = useMessage('course5.task1.intro');
  const parsedTaskOneIntro = parseMsgContent(taskOneIntroText);
  const questionOneArray = useMessage('course5.task1.answer_options');

  const content = breakpoint?.content ? (breakpoint.content as Content) : null;

  const breakpoints: Breakpoints = useMemo(() => {
    return [
      { from: 0, to: 767, content: { columns: 1 } },
      {
        from: 768,
        to: Number.MAX_SAFE_INTEGER,
        content: { columns: 2 },
      },
    ];
  }, []);

  return (
    <BreakpointListener breakpoints={breakpoints} onChange={setBreakpoint}>
      <div>
        <CourseTaskHeader questionNumber={1} totalQuestions={2} />
        <div style={{ marginTop: space.large, marginBottom: space.default }}>
          <CourseContent content={parsedTaskOneIntro} />
        </div>
        <RadioGroup
          columns={content?.columns}
          buttons
          onChange={(_ev, val) => {
            setShowQuestionOneResult(true);
            setValue(val);
          }}
          value={value}
        >
          {Object.keys(questionOneArray).map((val) => {
            return (
              <Option
                data-testid={`course5-task1-radio-${val}`}
                key={val}
                value={val}
                labelProps={{
                  style: {
                    padding: '20px',
                    alignItems: 'center',
                    border: 0,
                  },
                  className: 'investoracademy-radio-group-option',
                }}
              >
                <div className="investoracademy-radio-tile">
                  <p className="opds-styles-v1-text-body-xl investoracademy-radiotile-title">
                    <Message id={`${'course5.task1.answer_options'}.${val}.title`} />
                  </p>
                  <p className="opds-styles-v1-text-body-xs investoracademy-radiotile-body">
                    <Message id={`${'course5.task1.answer_options'}.${val}.body`} />
                  </p>
                </div>
              </Option>
            );
          })}
        </RadioGroup>
      </div>
      <div style={{ display: showQuestionOneResult ? 'block' : 'none' }}>
        <Image013
          className="investoracademy-small-svg"
          style={{
            marginTop: space.xxl,
          }}
          aria-hidden="true"
        />
        <div
          style={{
            paddingTop: space.default,
            paddingBottom: space.xl,
            lineHeight: '2.4rem',
            textAlign: 'center',
          }}
        >
          <div
            className="opds-styles-v1-text-title-small-strong"
            style={{ textAlign: 'center', color: '#EE5A00' }}
          >
            <Message id="answer" />
          </div>
          {value && (
            <>
              <h4 className="opds-styles-v1-text-title-medium">
                <Message id={`course5.task1.answer.${value}.title`} />
              </h4>
              <div
                className="opds-styles-v1-text-body-medium"
                style={{
                  paddingTop: space.small,
                }}
              >
                <p style={{ paddingBottom: '0' }}>
                  <Message id={`course5.task1.answer.${value}.body`} />
                </p>
              </div>
            </>
          )}
        </div>
        <TaskOneGraph selectedAnswer={value} />
        <p
          className="opds-styles-v1-text-label-default"
          style={{ display: 'flex', paddingBottom: space.default }}
        >
          <IconInformation style={{ paddingRight: '1rem' }} />
          <Message id="course5.task_one_graph.sources" />
        </p>
      </div>
    </BreakpointListener>
  );
};

export default TaskOne;
